export type LabKind = 'challenge' | 'machine';

export interface IAttachmentItem {
  name: string
  url: string
  description?: string
  kind: 'pdf' | 'link' | 'video' | 'file'
}

const isPdf = (attachment: any) => String(attachment?.name || '').includes('.pdf');

// Mesma regra das telas antigas: PDFs de write-up aparecem só no idioma do usuário
const matchesLanguage = (attachment: any, lang: string) => {
  if (attachment.type === 'video' || attachment.type === 'link' || !isPdf(attachment)) return true;
  if (attachment.type !== 'doc' && attachment.type !== 'machine') return false;
  const language = attachment.metadata?.language;
  return lang === 'pt' ? (language == null || language === 'pt') : language === 'en';
};

export const labAttachmentItems = (attachments: any[] | null | undefined, lang: string): IAttachmentItem[] =>
  (attachments || [])
    .filter(attachment => matchesLanguage(attachment, lang))
    .map(attachment => ({
      name: attachment.name,
      url: attachment.type === 'video' ? (attachment.metadata?.lesson_url || attachment.url) : attachment.url,
      description: attachment.filename,
      kind: attachment.type === 'link' ? 'link'
        : attachment.type === 'video' ? 'video'
          : isPdf(attachment) ? 'pdf' : 'file',
    }));

// Desafios mandam "categories" ({id, name}); máquinas mandam "tags"
export const labTags = (resource: any): string[] => {
  const raw: any[] = resource?.categories?.length ? resource.categories : (resource?.tags || []);
  const names = raw.map(tag => typeof tag === 'string' ? tag : tag?.name).filter(Boolean);
  return Array.from(new Set(names));
};

export const labLevel = (resource: any): string | null => {
  const level = String(resource?.difficulty || resource?.dificulty || '').toLowerCase();
  return ['easy', 'medium', 'hard', 'insane'].includes(level) ? level : null;
};

export const LEVEL_TONES: Record<string, string> = {
  easy: 'green',
  medium: 'blue',
  hard: 'amber',
  insane: 'red',
};

const attachmentKind = (attachment: any): IAttachmentItem['kind'] => {
  if (attachment.type === 'link') return 'link';
  if (attachment.type === 'video') return 'video';
  return isPdf(attachment) || String(attachment.name || '').toLowerCase().endsWith('.pdf') ? 'pdf' : 'file';
};

// Materiais de uma aula: anexos da aula, anexos dos desafios da aula (sem links) e o campo "links" separado por ";"
export const lessonMaterialItems = (lesson: any, externalLabel: string, lang: string): IAttachmentItem[] => {
  const challengeAttachments = (lesson?.challenges || [])
    .flatMap((challenge: any) => challenge?.attachments || [])
    .filter((attachment: any) => attachment && attachment.type !== 'link' && matchesLanguage(attachment, lang));

  const files: IAttachmentItem[] = [...(lesson?.attachments || []), ...challengeAttachments].map((attachment: any) => ({
    name: attachment.name,
    url: attachment.type === 'video' ? (attachment.metadata?.lesson_url || attachment.url) : attachment.url,
    description: attachment.filename || attachment.type,
    kind: attachmentKind(attachment),
  }));

  const links: IAttachmentItem[] = String(lesson?.links || '')
    .split(';')
    .map((link: string) => link.trim())
    .filter(Boolean)
    .map((link: string) => ({
      name: link.replace(/^https?:\/\//, ''),
      url: link,
      description: externalLabel,
      kind: 'link' as const,
    }));

  return [...files, ...links];
};
